/**
 * Pixel diff for the Tier 1 before/after snapshot viewer (task 3.8's console
 * side). Same technique as `extension/src/diff.ts` — a simple per-pixel
 * comparison with a small per-channel tolerance — implemented against a plain
 * DOM `<canvas>` here since this runs in a normal browser tab, not a Manifest
 * V3 service worker (which has no `document` and needs `OffscreenCanvas`
 * instead). Not imported from the extension package directly: that package
 * has its own tsconfig with no DOM canvas assumptions beyond what a service
 * worker offers, and pulling browser-tab-only APIs into it would be the wrong
 * direction of dependency.
 */

const CHANNEL_TOLERANCE = 12;

function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not decode image.'));
    img.src = dataUrl;
  });
}

export interface ImageDiffResult {
  diffPercent: number;
  changed: boolean;
}

export async function diffImages(aDataUrl: string, bDataUrl: string, maxDiffRatio = 0.02): Promise<ImageDiffResult> {
  const [a, b] = await Promise.all([loadImage(aDataUrl), loadImage(bDataUrl)]);

  if (a.naturalWidth !== b.naturalWidth || a.naturalHeight !== b.naturalHeight) {
    return { diffPercent: 100, changed: true };
  }

  const canvas = document.createElement('canvas');
  canvas.width = a.naturalWidth;
  canvas.height = a.naturalHeight;
  const ctx = canvas.getContext('2d')!;

  ctx.drawImage(a, 0, 0);
  const pixelsA = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  ctx.drawImage(b, 0, 0);
  const pixelsB = ctx.getImageData(0, 0, canvas.width, canvas.height).data;

  const total = canvas.width * canvas.height;
  let differing = 0;
  for (let i = 0; i < pixelsA.length; i += 4) {
    const dr = Math.abs(pixelsA[i] - pixelsB[i]);
    const dg = Math.abs(pixelsA[i + 1] - pixelsB[i + 1]);
    const db = Math.abs(pixelsA[i + 2] - pixelsB[i + 2]);
    if (dr > CHANNEL_TOLERANCE || dg > CHANNEL_TOLERANCE || db > CHANNEL_TOLERANCE) differing++;
  }

  const diffPercent = (differing / total) * 100;
  return { diffPercent, changed: differing / total > maxDiffRatio };
}
